import type { JSX } from "react";
import { SOURCES } from "../constants/maps-constants";
import { PingQueryParameterType } from "../utils/setQueryParameter";

export type LatLngType = { lat: number; lng: number };

export type ElevationLocation = {
  point?: LatLngType;
  elevation?: number;
};

export type ElevationData = {
  point: LatLngType;
  elevation: string;
};

export type Polygon = {
  outer: LatLngType[];
  holes: LatLngType[][];
};

export enum ShapeFilterType {
  RECTANGLE = "RECTANGLE",
  CIRCLE = "CIRCLE",
  POLYGON = "POLYGON",
}

type RectangleShape = {
  type: ShapeFilterType.RECTANGLE;
  corner1: LatLngType;
  corner2: LatLngType;
};

type CircleShape = {
  type: ShapeFilterType.CIRCLE;
  center: LatLngType;
  radius: number;
};

type PolygonShape = {
  type: ShapeFilterType.POLYGON;
  points: LatLngType[];
};

export type ShapeFilter = RectangleShape | CircleShape | PolygonShape;

export type AOA_SEVERITY_COLORS = "DEFAULT" | "WARNING" | "ERROR" | "CRITICAL";

export type RegionTooltipFormat =
  | "DEFAULT"
  | "PERCENTAGE"
  | "DECIMALS_1"
  | "DECIMALS_2"
  | "EASI"
  | "INTEGER"
  | "CURRENCY"
  | "DATE"
  | "TIMESTAMP_MS";

export type BuildingPanelField = {
  k: string;
  v: string | number;
  t?: string;
  vt?: string | number;
  c?: AOA_SEVERITY_COLORS;
  source?: string;
  sourceField?: string;
};

export type BuildingPanelSection = {
  title: string;
  type: "MAPS" | "PLI";
  values: BuildingPanelField[];
};

export type BuildingPanelMapsExtras = {
  type: "MAPS";
  fns: Record<string, Function>;
  displacementMode: boolean;
  aoaResponse: any;
  fieldOverrides: Record<string, any>;
  integration_overrides: Record<string, any>;
};

export type BuildingPanelPLIExtras = {
  type: "PLI";
  aoaResponse: any;
  integration_overrides: Record<string, any>;
};

export type BuildingPanelExtras =
  | BuildingPanelMapsExtras
  | BuildingPanelPLIExtras;

export type BuildingPanelTopData = {
  buildingName: string;
  buildingOriginalAddress: string;
  buildingPingAddress: string;
  matchLevel?: string;
  constructedAddress?: string | null;
};

export type PolicyBuildingDataAttributeType = {
  zip: string;
  city: string;
  route: string;
  state: string;
  county: string;
  street: string;
  country: string;
  eq_zone: string;
  latitude: number;
  bldg_name: string;
  longitude: number;
  wind_tier?: number | null; // Optional field
  const__desc: string;
  eq_sub_zone: string;
  full_address: string;
  insured__name: string;
  address_number: string;
  icm__wind_tier?: number | null; // Optional field
  location_group: number;
  const__code_air: number;
  const__code_atc: string;
  const__code_iso: number;
  const__code_rms: string;
  const__desc_air: string;
  const__desc_atc: string;
  const__desc_iso: string;
  const__desc_rms: string;
  fema_flood_zone: string;
  const__bldg_area: number;
  const__desc_ping: string;
  const__roof_type: string;
  county_fips_code: string;
  google_maps_link: string;
  limits__bi_limit: number;
  ownership_status: string;
  limits__bpp_limit: number;
  limits__bpp_value: number;
  const__num_stories: number;
  const__code_for_rms: string;
  fema_flood_map_link: string;
  limits__total_limit: number;
  limits__total_value: number;
  occupancy__code_air: number;
  occupancy__code_atc: string;
  occupancy__desc_air: string;
  occupancy__desc_atc: string;
  protect__sprinklers: boolean;
  const__category_ping: string;
  const__roof_covering: string;
  icm__fire_sprinklers: string;
  occupancy__desc_ping: string;
  const__fire_desc_ping: string;
  bldg_street_descriptor: string;
  const__bldg_year_built: number;
  google_earth_maps_link: string;
  limits__building_limit: number;
  limits__building_value: number;
  limits__contents_limit: number;
  occupancy__detail_ping: string;
  limits__inventory_limit: number;
  const__bldg_year_updated: number;
  limits__total_rp_and_bpp: number;
  occupancy__category_ping: string;
  protect__fire_alarm_type: string;
  protect__pct_sprinklered: number;
  limits__bpp_excl_equipment: number;
  limits__bpp_excl_inventory: number;
  limits__insurance_to_value: number;
  ping_geo__google_maps_link: string;
  hazardhub__google_maps_link: string;
  limits__orig_limits: {
    [key: string]: number;
  };
  confident_geocoding_disagreement: number;
  lightboxre_geo__google_maps_link: string;
  limits__bpp_value_excl_equipment: number;
  limits__orig_limits_bus_interruption: number;
  limits__bpp_excl_inventory_and_equipment: number;
  limits__orig_limits_building_improvements: number;
  limits__orig_limits_furnitures_and_fixtures: number;
  limits__tenant_improvement_and_betterments_limit: number;
  limits__orig_limits_inventory_average_stock_value_finished_goods: number;
  slosh_zone?: string;
  fema_wildfire?: string;
  ping_geo__match_level: string;
  orig_sov_geocoding_disagreement?: number;
};

export type PolicyBuildingType = {
  policy: number;
  building_counter?: number;
  item_key: string;
  amount_subject: number;
  tiv: number;
  data: PolicyBuildingDataAttributeType;
  const_code_pcg: string;
  original: {
    latitude: number;
    longitude: number;
    full_address?: string;
  };
  external_data?: {
    FNRI?: {
      wfir_riskr?: string;
    };
    PG?: {
      formatted_address?: string;
      match_level?: string;
    };
  };
  street?: string;
  route?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  county?: string;
  limits_total_limit: number;
  limits_building_limit: number;
  occupancy_desc_atc?: string;
  occupancy_desc_air?: string;
  const_desc_atc?: string;
  const_desc_air?: string;
  const_bldg_year_built?: number;
  const_bldg_area?: number;
  const_num_stories?: number;
  const_roof_year_updated?: number;
  protect_sprinklers?: boolean;
  fema_flood_zone?: string;
  slosh_zone?: string;
  eq_zone?: string;
  latitude?: number;
  longitude?: number;
  bldg_name?: string;
  ping_geo_match_level?: string;
};

export type SourceKey = (typeof SOURCES)[keyof typeof SOURCES];

/**
 * Describes a floating panel in the PingMapWorkspace. A panel contains tools or
 * information that the user can interact with.
 */
export type PingMapWorkspacePanelDescription = {
  title: string;
  icon: string;
  renderContent: () => JSX.Element;
  // Returning null renders no header bar, for panels that carry their own controls.
  renderHeader?: (title: string, onClose: () => void) => JSX.Element | null;
};

/**
 * Describes a floating panel in the PingMapWorkspace that is anchored to the
 * right side of the screen.
 */
export type PingMapWorkspaceRightPanelDescription =
  PingMapWorkspacePanelDescription & {
    id: PingMapWorkspaceRightPanelId;
    widthRem: number;
  };

/**
 * Describes a floating panel in the PingMapWorkspace that is anchored to the
 * bottom of the screen.
 */
export type PingMapWorkspaceBottomPanelDescription =
  PingMapWorkspacePanelDescription & {
    id: PingMapWorkspaceBottomPanelId;
    heightRem: number;
  };

export type PingMapWorkspaceRightPanelId =
  | null
  | "buildings"
  | "legend"
  | "exposed-limits";

export type PingMapWorkspaceBottomPanelId =
  | null
  | "buildings-list"
  | "breakdown-panel";

export type SovBuildingType = {
  lat: number;
  lng: number;
  orig_lat: number;
  orig_lng: number;

  limits_total_limit?: number;
  const_bldg_area?: number;

  lt?: string;
  confidence: number;
  precision: number;
  s: string;
  uid: string;

  /** Total insured value */
  tiv: number;

  /** Construction type */
  cd: string;

  /** Occupancy */
  od: string;

  /** Zip code */
  zipcode: string;

  /** Flood zone */
  fz: string;

  /** Year built */
  yb: number;

  /** Wind tiers */
  wt: string | null;

  /**  Year built band */
  ybb: string | null;

  /** Country */
  c: string;

  /** State */
  st: string;

  /** County */
  county: string;

  /** Street */
  street?: string;

  /** City */
  city?: string;

  /** State */
  state?: string;

  /** Zip code */
  zip?: string;

  /** Full address */
  fullAddress?: string;

  /** Building name */
  bldg_name?: string;

  /** Building counter */
  building_counter?: number | string;

  /** Location group */
  location_group?: number | string;

  /** Location group item */
  location_group_item?: number | string;

  /** Number of stories */
  ns: number;

  /** Sprinkler */
  spr: boolean;

  /** EQ zone */
  eqz: string;

  /** EQ sub zone */
  eqsz: string;

  /** Reliability */
  reliability: {
    Construction: string;
    Geocoding: string;
    Limit: string;
    Location: string;
    Occupancy: string;
    Overall: string;
  };

  /** Displacement */
  displacement: number | null;

  // The next set of fields are calculated on the frontend and added to the
  // building by the SOV reducer.

  /** Is this building in Florida? */
  is_florida?: boolean;

  /** Does this fall in the Broward, Miami-Dade, or Palm Beach counties? */
  is_tricounty?: boolean;

  /** Is the EQ Zone set to "CA"? */
  is_eq_cat?: boolean;

  /** Is this building in a high hazard flood zone? */
  high_hazard_flood?: boolean;

  /** Is this a wood frame building? */
  is_frame?: boolean;

  /** Match level */
  match_level?: string;
};

export type AOAResponseType = {
  id: string;
  location_data: Record<string, any>;
};

export type PingAoaBuildingLookupRequest = {
  address: string;
  lat?: number;
  lng?: number;
  source: string;
  pli_auto_pull_integrations?: string[];
  dtc_include_coastline_within_miles?: number;
  dtc_return_connected_coastlines?: boolean;
  const__bldg_area?: number;
  const__desc_ping?: string;
  occupancy__desc_ping?: string;
  include_raw_response?: boolean;
  address_line_1?: string;
  limits__building_limit?: number;
  const__bldg_year_built?: number;
  limits__bi_limit?: number;
  limits__bpp_limit?: number;
  const__num_stories?: number;
  occupancy__code_air?: string | number;
  const__code_air?: string | number;
  occupancy__code_atc?: string;
  const__code_rms?: string;
  air__roof_covering?: string;
  air__wall_type?: string;
  geocode_confidence?: number;
  geocode_precision?: number;
  lightbox_parcel_id?: string;
  deductible_amount?: number;
  city?: string;
  state?: string;
  county?: string;
  postal_code?: string;
};

export type PolicyQueryInputs = {
  sovid: string;
  lat1?: number;
  lng1?: number;
  lat2?: number;
  lng2?: number;
  filters?: Record<string, PingQueryParameterType>;
  attach?: number;
  layerLimit?: number;
  shapeFilter?: ShapeFilter | null;
};

export type BoundingBoxType = {
  lat1: number;
  lng1: number;
  lat2: number;
  lng2: number;
};

export type PolicyBuildingsQueryInputs = PolicyQueryInputs & {
  search?: string;
  // Only these building schema columns of each building.
  fields?: string[];
  accessToken?: string;
  readOnlyToken?: string;
  authScope?: string;
};

export type PolicyBuildingListItem = {
  item_key: string;
  data?: PolicyBuildingDataAttributeType;
  latitude?: number | null;
  longitude?: number | null;
  ping_geo_match_level?: string;
  [key: string]: unknown;
};

export type PolicyBuildingsPage = {
  results: PolicyBuildingListItem[];
  next_cursor: string | null;
  has_remaining: boolean;
  sync_version: number;
};

export type ActionBarButtonIds =
  | "filters"
  | "shape-filters"
  | "measurements"
  | "overlays";

import type { IconName } from "@repo/ping-design";

export type ButtonConfig = {
  id: ActionBarButtonIds;
  label: string;
  iconName: IconName;
  iconOrientation: "left" | "right";
  disabled?: boolean;
};

export type PingH3HexType = {
  center: [number, number];
  boundry: Array<[number, number]>;
  area: number;
  limits__total_limit: number;
  amount_subject?: number;
  location_count?: number;
  opacity: number;
  color: string;
  h3_index: string;
  diameter: number;
};

export type TransformedPolicyAPIResponse = {
  buildings: SovBuildingType[] | null;
  hexes: PingH3HexType[] | null;
};

export type RegionTooltipAttribute = {
  field: string;
  label: string;
  format?: RegionTooltipFormat;
};

export type LayerConfig = {
  attributes: RegionTooltipAttribute[];
  title: string;
};

export type GeocoderType = {
  address?: string;
  confidence?: number | string;
  lat: number;
  lng: number;
  precision?: number | string;
  source: string;
  consulted_datasources?: string[] | null;
  source_desc?: string | null;
  merged?: Array<{ source: string }>;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
};

export type AnnotationCoordinates = {
  latitude: number;
  longitude: number;
};

export type AnnotationLocation = {
  sheet_row_number: number | string;
  sheet_name: string;
  annotations: AnnotationCoordinates;
};

export type AnnotationData = {
  locations: AnnotationLocation[];
};

// What a pin move does to values that depend on where the building is.
export type LocationChange = "clear" | "keep" | "recalculate";

export type AddAnnotationRequest = {
  sovid: string | null | undefined;
  item_key?: string;
  sheet_row_number?: string;
  sheet_name?: string;
  lat?: number | null;
  lng?: number | null;
  on_location_change?: LocationChange;
};

/** One column of the buildings grid, keyed by building attr. */
export type BuildingSchemaColumn = {
  key: string;
  caption: string;
  group: string | null;
  // ColumnDataType name, e.g. "CURRENCY", "NUMBER", "YEAR", "TEXT".
  datatype: string | null;
  // Vendor abbreviation; null for SOV data.
  source: string | null;
  derived: boolean;
  default_visible: boolean;
  // null when the column isn't editable.
  editor: "dropdown" | "money" | "numeric" | "text" | null;
  // How the grid draws the values; null for the default formatting by datatype and options.
  // "percent": a fraction drawn as a percentage (0.25 is 25%).
  display: "geocode_match_level" | "link" | "percent" | null;
  // What must be in effect in Maps for the column to show, e.g. a layer for Amt Exposed.
  requires: "layer" | null;
  // Excel column width: characters of "0", i.e. CSS `ch`. null sizes the column to its content.
  width: number | null;
  // Column-settings toggles that show or hide this column with others, outermost first; null for none.
  groups: string[] | null;
  options?: { value: string | number; label: string }[];
};

/** The buildings grid's columns for this viewer, in order; the backend decides which. */
export type BuildingSchema = {
  // Saved column layouts are kept per layout key.
  layout_key: string;
  columns: BuildingSchemaColumn[];
};

export type AddAnnotationResponse = {
  success: boolean;
};

export type BulkAnnotationRequest = {
  sovid: string | null | undefined;
  item_keys: string[];
  data: Record<string, string | number | null>;
  // policy_buildings fields to return for each edited building.
  fields?: string[];
};

export type BulkAnnotationResponse = {
  success: boolean;
  updated: number;
  // Every value each edit changed, edited and derived, by item_key then attr.
  changes: Record<string, Record<string, unknown>>;
  // The edited buildings as policy_buildings returns them.
  buildings: any[];
};
